import { useEffect, useRef, useState } from "react";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { api } from "@/lib/api";
import { Sparkles, ArrowUp, Loader2, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import MarkdownMessage from "@/components/copilot/MarkdownMessage";

export default function AICopilot() {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [sessions, setSessions] = useState([]);
    const scrollRef = useRef(null);

    useEffect(() => {
        api.get("/copilot/suggestions").then(({ data }) => setSuggestions(data.suggestions || []));
        api.get("/copilot/sessions").then(({ data }) => setSessions(data));
    }, []);
    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

    const send = async (text) => {
        const m = (text ?? input).trim();
        if (!m || busy) return;
        setMessages((arr) => [...arr, { role: "user", content: m }]);
        setInput(""); setBusy(true);
        try {
            const { data } = await api.post("/copilot/chat", { message: m, session_id: sessionId, context: { screen: "/copilot" } });
            setSessionId(data.session_id);
            setMessages((arr) => [...arr, { role: "assistant", content: data.message.content }]);
        } finally { setBusy(false); }
    };

    const loadSession = async (sid) => {
        const { data } = await api.get(`/copilot/messages/${sid}`);
        setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        setSessionId(sid);
    };

    const newSession = () => { setMessages([]); setSessionId(null); };

    return (
        <>
            <PageHeader kicker="INTELLIGENCE" title="AI Copilot" subtitle="A finance analyst built into your workspace. Powered by Claude Sonnet 4.5." />
            <PageBody className="!py-0">
                <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-220px)] py-6">
                    <aside className="bg-card border border-border rounded-md p-3 overflow-y-auto">
                        <Button onClick={newSession} className="w-full mb-3" size="sm" data-testid="copilot-new-session">+ New conversation</Button>
                        <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2 px-1">RECENT</div>
                        {sessions.length === 0 && <div className="text-[12px] text-muted-foreground px-2">No history yet.</div>}
                        {sessions.map((s) => (
                            <button key={s.session_id} onClick={() => loadSession(s.session_id)} className={cn("w-full text-left text-[12.5px] px-2 py-2 rounded-md hover:bg-muted/60 truncate", sessionId === s.session_id && "bg-muted")}>
                                <MessageSquare className="w-3.5 h-3.5 inline mr-1.5 text-muted-foreground" />
                                {s.title}
                            </button>
                        ))}
                    </aside>
                    <main className="bg-card border border-border rounded-md flex flex-col overflow-hidden">
                        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
                            {messages.length === 0 && (
                                <div className="max-w-[640px] mx-auto text-center pt-12">
                                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-md bg-foreground text-background mb-4">
                                        <Sparkles className="w-6 h-6 text-gold" />
                                    </div>
                                    <h2 className="font-serif text-3xl">How can I help you today?</h2>
                                    <p className="text-[13px] text-muted-foreground mt-2">I have full access to your books — invoices, bills, banking, GST, and inventory.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-6">
                                        {suggestions.map((s) => (
                                            <button key={s} onClick={() => send(s)} className="text-left px-4 py-3 rounded-md border border-border bg-muted/30 hover:bg-muted hover:border-foreground/40 text-[13px]" data-testid={`copilot-page-suggestion-${s.slice(0, 16)}`}>
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                                    <div className={cn(
                                        "max-w-[80%] rounded-md px-4 py-3 text-[13.5px] leading-relaxed",
                                        m.role === "user" ? "bg-foreground text-background whitespace-pre-wrap" : "bg-muted/60 text-foreground border border-border"
                                    )}>
                                        {m.role === "assistant" ? <MarkdownMessage>{m.content}</MarkdownMessage> : m.content}
                                    </div>
                                </div>
                            ))}
                            {busy && <div className="flex"><div className="bg-muted/60 border border-border rounded-md px-3 py-2 inline-flex items-center gap-2 text-[12px] text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing your books…</div></div>}
                        </div>
                        <div className="border-t border-border p-4">
                            <div className="flex items-end gap-2 max-w-[860px] mx-auto">
                                <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Ask anything about your finances…" rows={2} data-testid="copilot-page-input" className="flex-1 resize-none bg-muted/40 border border-border rounded-md px-3 py-2 text-[13px] focus:outline-none" />
                                <Button onClick={() => send()} size="icon" disabled={busy || !input.trim()} data-testid="copilot-page-send"><ArrowUp className="w-4 h-4" /></Button>
                            </div>
                        </div>
                    </main>
                </div>
            </PageBody>
        </>
    );
}
